import { useEffect, useState } from "react";
import { Button } from "@legal-500/legal500-design-system";
import { relationships } from "@/api/addressBook.service";
import type { FirmRelationship, FirmRelationshipType } from "@/api/addressBook.service";
import { CompanyPicker } from "@/components/Profiles/ListFilters/CompanyPicker";
import styles from "../AddressBook.module.css";

type CompanyRelationshipsProps = {
  companyId: string;
  businessType: string | null;
};

type SectionState = {
  pickedId: string | null;
  error: string | null;
  isAdding: boolean;
};

function emptySection(): SectionState {
  return { pickedId: null, error: null, isAdding: false };
}

export function CompanyRelationships({ companyId, businessType }: CompanyRelationshipsProps) {
  const [outgoing, setOutgoing] = useState<FirmRelationship[]>([]);
  const [incoming, setIncoming] = useState<FirmRelationship[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [affiliationsSection, setAffiliationsSection] = useState<SectionState>(emptySection());
  const [memberNetworkSection, setMemberNetworkSection] = useState<SectionState>(emptySection());

  const isNetwork = businessType === "law_firm_network";

  useEffect(() => {
    setLoadError(null);
    relationships
      .getAll(companyId)
      .then(res => {
        setOutgoing(res.outgoing);
        setIncoming(res.incoming);
      })
      .catch(err => {
        setLoadError(err instanceof Error ? err.message : String(err));
      });
  }, [companyId]);

  const affiliations = [
    ...outgoing.filter(r => r.type === "affiliated_with"),
    ...incoming.filter(r => r.type === "affiliated_with"),
  ];

  const members = incoming.filter(r => r.type === "member_of");
  const networks = outgoing.filter(r => r.type === "member_of");

  async function handleAdd(
    type: FirmRelationshipType,
    pickedId: string | null,
    setSectionState: React.Dispatch<React.SetStateAction<SectionState>>,
  ) {
    if (!pickedId) return;
    setSectionState(s => ({ ...s, isAdding: true, error: null }));
    try {
      await relationships.create(companyId, { target_id: pickedId, type });
      const res = await relationships.getAll(companyId);
      setOutgoing(res.outgoing);
      setIncoming(res.incoming);
      setSectionState(emptySection());
    } catch (err) {
      setSectionState(s => ({
        ...s,
        isAdding: false,
        error: err instanceof Error ? err.message : String(err),
      }));
    }
  }

  async function handleRemove(id: number) {
    try {
      await relationships.delete(companyId, id);
      const res = await relationships.getAll(companyId);
      setOutgoing(res.outgoing);
      setIncoming(res.incoming);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <div>
      {loadError && <span className={styles.errorText}>{loadError}</span>}

      <section role="region" aria-label="Affiliations" className={styles.section}>
        <h2 className={styles.sectionTitle}>Affiliations</h2>
        {affiliationsSection.error && (
          <span className={styles.errorText}>{affiliationsSection.error}</span>
        )}
        {affiliations.length === 0 ? (
          <p className={styles.emptyText}>No affiliations yet.</p>
        ) : (
          <ul className={styles.rolesList}>
            {affiliations.map(r => (
              <li
                key={r.id}
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
              >
                <span>{r.other_firm.name}</span>
                <Button
                  type="button"
                  variant="outlined"
                  color="default"
                  size="small"
                  onClick={() => handleRemove(r.id)}
                >
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        )}
        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            gap: "var(--spacing-3)",
            marginTop: "var(--spacing-4)",
          }}
        >
          <div style={{ flex: 1 }}>
            <CompanyPicker
              value={affiliationsSection.pickedId}
              onChange={id => setAffiliationsSection(s => ({ ...s, pickedId: id }))}
            />
          </div>
          <Button
            type="button"
            variant="contained"
            color="primary"
            disabled={affiliationsSection.isAdding}
            onClick={() =>
              handleAdd("affiliated_with", affiliationsSection.pickedId, setAffiliationsSection)
            }
          >
            Add
          </Button>
        </div>
      </section>

      {isNetwork ? (
        <section
          role="region"
          aria-label="Members"
          className={styles.section}
          style={{ marginTop: "var(--spacing-4)" }}
        >
          <h2 className={styles.sectionTitle}>Members</h2>
          {memberNetworkSection.error && (
            <span className={styles.errorText}>{memberNetworkSection.error}</span>
          )}
          {members.length === 0 ? (
            <p className={styles.emptyText}>No members yet.</p>
          ) : (
            <ul className={styles.rolesList}>
              {members.map(r => (
                <li
                  key={r.id}
                  style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
                >
                  <span>{r.other_firm.name}</span>
                  <Button
                    type="button"
                    variant="outlined"
                    color="default"
                    size="small"
                    onClick={() => handleRemove(r.id)}
                  >
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              gap: "var(--spacing-3)",
              marginTop: "var(--spacing-4)",
            }}
          >
            <div style={{ flex: 1 }}>
              <CompanyPicker
                value={memberNetworkSection.pickedId}
                onChange={id => setMemberNetworkSection(s => ({ ...s, pickedId: id }))}
              />
            </div>
            <Button
              type="button"
              variant="contained"
              color="primary"
              disabled={memberNetworkSection.isAdding}
              onClick={() =>
                handleAdd("member_of", memberNetworkSection.pickedId, setMemberNetworkSection)
              }
            >
              Add
            </Button>
          </div>
        </section>
      ) : (
        <section
          role="region"
          aria-label="Networks"
          className={styles.section}
          style={{ marginTop: "var(--spacing-4)" }}
        >
          <h2 className={styles.sectionTitle}>Networks</h2>
          {memberNetworkSection.error && (
            <span className={styles.errorText}>{memberNetworkSection.error}</span>
          )}
          {networks.length === 0 ? (
            <p className={styles.emptyText}>No networks yet.</p>
          ) : (
            <ul className={styles.rolesList}>
              {networks.map(r => (
                <li
                  key={r.id}
                  style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
                >
                  <span>{r.other_firm.name}</span>
                  <Button
                    type="button"
                    variant="outlined"
                    color="default"
                    size="small"
                    onClick={() => handleRemove(r.id)}
                  >
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              gap: "var(--spacing-3)",
              marginTop: "var(--spacing-4)",
            }}
          >
            <div style={{ flex: 1 }}>
              <CompanyPicker
                value={memberNetworkSection.pickedId}
                onChange={id => setMemberNetworkSection(s => ({ ...s, pickedId: id }))}
                businessType="law_firm_network"
              />
            </div>
            <Button
              type="button"
              variant="contained"
              color="primary"
              disabled={memberNetworkSection.isAdding}
              onClick={() =>
                handleAdd("member_of", memberNetworkSection.pickedId, setMemberNetworkSection)
              }
            >
              Add
            </Button>
          </div>
        </section>
      )}
    </div>
  );
}
